import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGuestName, setNewGuestName] = useState('');
  const [newMaxGuests, setNewMaxGuests] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editGuestName, setEditGuestName] = useState('');
  const [editMaxGuests, setEditMaxGuests] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchInvites();
  }, [navigate]);

  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/invites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(response.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } else {
        setError('Failed to load invites');
        setLoading(false);
      }
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!newGuestName.trim()) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post('/api/admin/invites',
        { guestName: newGuestName, maxGuests: newMaxGuests },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewGuestName('');
      setNewMaxGuests(1);
      setShowCreateForm(false);
      fetchInvites();
    } catch (err) {
      setError('Failed to create invite');
    }
  };

  const handleDeleteInvite = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invite?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/invites/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInvites();
    } catch (err) {
      setError('Failed to delete invite');
    }
  };

  const startEditing = (invite) => {
    setEditingId(invite.id);
    setEditGuestName(invite.guestName);
    setEditMaxGuests(invite.maxGuests || 1);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditGuestName('');
    setEditMaxGuests(1);
  };

  const handleUpdateInvite = async (id) => {
    if (!editGuestName.trim()) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/admin/invites/${id}`,
        { guestName: editGuestName, maxGuests: editMaxGuests },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      setEditGuestName('');
      setEditMaxGuests(1);
      fetchInvites();
    } catch (err) {
      setError('Failed to update invite');
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/#/rsvp/${token}`;

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => {
          alert('Invite link copied to clipboard!');
        })
        .catch(() => {
          // Fallback for mobile/older browsers
          fallbackCopy(link);
        });
    } else {
      // Fallback for mobile/older browsers
      fallbackCopy(link);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alert('Invite link copied to clipboard!');
      } else {
        // If copy fails, show the link so user can manually copy
        const userPrompt = prompt('Copy this invite link:', text);
      }
    } catch (err) {
      // If all else fails, show the link
      prompt('Copy this invite link:', text);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const exportToCSV = () => {
    const headers = ['Guest Name', 'Max Guests', 'Status', 'Attending', 'Number of Guests', 'Additional Guests', 'Phone', 'Message'];
    const rows = invites.map(invite => [
      invite.guestName,
      invite.maxGuests || 1,
      invite.hasResponded ? 'Responded' : 'Pending',
      invite.rsvp?.attending || 'N/A',
      invite.rsvp?.numberOfGuests || 'N/A',
      invite.rsvp?.guestNames || 'N/A',
      invite.rsvp?.phone || 'N/A',
      invite.rsvp?.message || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wedding-rsvps.csv';
    a.click();
  };

  const filteredInvites = invites.filter(invite => {
    const matchesSearch = invite.guestName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'responded' && invite.hasResponded) ||
      (filterStatus === 'pending' && !invite.hasResponded) ||
      (filterStatus === 'attending' && invite.rsvp?.attending === 'yes') ||
      (filterStatus === 'declined' && invite.rsvp?.attending === 'no');
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: invites.length,
    responded: invites.filter(i => i.hasResponded).length,
    attending: invites.filter(i => i.rsvp?.attending === 'yes').length,
    declined: invites.filter(i => i.rsvp?.attending === 'no').length,
    totalGuests: invites.reduce((sum, i) => sum + (parseInt(i.rsvp?.numberOfGuests) || 0), 0)
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="wide-container">
        <div className="dashboard-header">
          <div>
            <h1>Admin Dashboard</h1>
            <p className="dashboard-subtitle">Manage wedding invitations and RSVPs</p>
          </div>
          <button onClick={handleLogout} className="btn btn-secondary">
            Logout
          </button>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Invites</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
          <div className="stat-card">
            <h3>Responded</h3>
            <p className="stat-number">{stats.responded}</p>
          </div>
          <div className="stat-card">
            <h3>Attending</h3>
            <p className="stat-number attending">{stats.attending}</p>
          </div>
          <div className="stat-card">
            <h3>Declined</h3>
            <p className="stat-number declined">{stats.declined}</p>
          </div>
          <div className="stat-card">
            <h3>Total Guests</h3>
            <p className="stat-number">{stats.totalGuests}</p>
          </div>
        </div>

        <div className="dashboard-actions">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn btn-primary"
          >
            {showCreateForm ? 'Cancel' : 'Create New Invite'}
          </button>
          <button onClick={exportToCSV} className="btn btn-secondary">
            Export to CSV
          </button>
        </div>

        {showCreateForm && (
          <div className="card">
            <form onSubmit={handleCreateInvite} className="create-invite-form">
              <div className="form-group">
                <label htmlFor="guestName">Guest Name</label>
                <input
                  type="text"
                  id="guestName"
                  value={newGuestName}
                  onChange={(e) => setNewGuestName(e.target.value)}
                  placeholder="Enter guest name"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="maxGuests">Maximum Number of Guests</label>
                <input
                  type="number"
                  id="maxGuests"
                  value={newMaxGuests}
                  onChange={(e) => setNewMaxGuests(parseInt(e.target.value))}
                  min="1"
                  max="20"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary">
                Generate Invite Link
              </button>
            </form>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}

        <div className="card">
          <div className="filters">
            <input
              type="text"
              placeholder="Search by guest name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Invites</option>
              <option value="responded">Responded</option>
              <option value="pending">Pending</option>
              <option value="attending">Attending</option>
              <option value="declined">Declined</option>
            </select>
          </div>

          <div className="invites-table">
            <table>
              <thead>
                <tr>
                  <th>Guest Name</th>
                  <th>Max Guests</th>
                  <th>Status</th>
                  <th>Attending</th>
                  <th>Guests</th>
                  <th>Additional Info</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvites.map(invite => (
                  <tr key={invite.id}>
                    <td>
                      {editingId === invite.id ? (
                        <input
                          type="text"
                          value={editGuestName}
                          onChange={(e) => setEditGuestName(e.target.value)}
                          className="inline-edit-input"
                          autoFocus
                        />
                      ) : (
                        <strong>{invite.guestName}</strong>
                      )}
                    </td>
                    <td>
                      {editingId === invite.id ? (
                        <input
                          type="number"
                          value={editMaxGuests}
                          onChange={(e) => setEditMaxGuests(parseInt(e.target.value))}
                          min="1"
                          max="20"
                          className="inline-edit-input"
                          style={{ width: '60px' }}
                        />
                      ) : (
                        invite.maxGuests || 1
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${invite.hasResponded ? 'responded' : 'pending'}`}>
                        {invite.hasResponded ? 'Responded' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      {invite.rsvp?.attending === 'yes' && <span className="attendance-yes">Yes</span>}
                      {invite.rsvp?.attending === 'no' && <span className="attendance-no">No</span>}
                      {!invite.rsvp?.attending && '-'}
                    </td>
                    <td>{invite.rsvp?.numberOfGuests || '-'}</td>
                    <td>
                      {invite.hasResponded && (
                        <div className="additional-info">
                          {invite.rsvp?.phone && <div><small>Phone: {invite.rsvp.phone}</small></div>}
                          {invite.rsvp?.guestNames && <div><small>Guests: {invite.rsvp.guestNames}</small></div>}
                          {invite.rsvp?.message && <div><small>Message: {invite.rsvp.message}</small></div>}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {editingId === invite.id ? (
                          <>
                            <button
                              onClick={() => handleUpdateInvite(invite.id)}
                              className="btn-icon btn-save"
                              title="Save changes"
                            >
                              ✓
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="btn-icon btn-cancel"
                              title="Cancel editing"
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            {!invite.hasResponded && (
                              <button
                                onClick={() => startEditing(invite)}
                                className="btn-icon btn-edit"
                                title="Edit invite"
                              >
                                ✏️
                              </button>
                            )}
                            <button
                              onClick={() => copyInviteLink(invite.token)}
                              className="btn-icon"
                              title="Copy invite link"
                            >
                              📋
                            </button>
                            <button
                              onClick={() => handleDeleteInvite(invite.id)}
                              className="btn-icon btn-delete"
                              title="Delete invite"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredInvites.length === 0 && (
              <p className="no-results">No invites found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
